import { TRESPONSE_LotListing, TStockType } from '@monorepo/types';
import * as moment from 'moment';
import { ModifiersZTableModel } from '../table.modifier';
import { TTableCellTree } from '../table.type';
import { LOT_TABLE_MODEL } from './ba-app/LOT_TABLE_MODEL';

export const FABRIC_TRANSACTION_LOT_MODEL = {
  createModel: (
    items: TRESPONSE_LotListing['items'],
    headerConfig: TRESPONSE_LotListing['headerConfig'],
    supportedFilters: TRESPONSE_LotListing['supportedFilters'],
    isColorFieldEnabled: boolean,
    stockType: TStockType,
  ) => {
    const isPlmExists = items.length > 0 && !!items[0].metadata.plm;
    const isPackedFilterAvailable = supportedFilters.find((filter) => filter.filterType === 'PACKED');
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'LAST UPDATED TIME',
            stickyType: 'LEFT',
          },
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'supplierName') || 'SUPPLIER',
          },

          ...(isPlmExists
            ? [
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'plm') || 'PLM NO.',
                } as TTableCellTree,
              ]
            : []),

          ...(stockType === TStockType.WITH_BCTX
            ? ([
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'buyer') || 'BUYER',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'po') || 'PO NO.',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'style') || 'STYLE',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'materialCode') || 'Material Code / PDM',
                },
              ] as TTableCellTree[])
            : ([
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'itemName') || 'ITEM NAME',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'itemCode') || 'ITEM CODE',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'lotGrnNumbers') || 'GRN No.',
                },
              ] as TTableCellTree[])),

          // Show packed column if packed filter is available
          ...(isPackedFilterAvailable
            ? [
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'packed') || 'PACKED',
                } as TTableCellTree,
              ]
            : []),

          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'fabricVariant') || 'FAB VARIANT',
          },
          ...(isColorFieldEnabled && stockType === TStockType.WITH_BCTX
            ? [
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'color') || 'COLOR',
                } as TTableCellTree,
              ]
            : []),
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'fabricColor') || 'FABRIC COLOR',
          },
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'fabricType') || 'FAB TYPE',
          },
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'lotNo') || 'LOT NO/BATCH NO.',
          },
          {
            type: 'TEXT_CELL',
            text: 'INSPECTED/TO BE INSPECTED',
            stickyType: 'RIGHT',
          },
          {
            type: 'TEXT_CELL',
            text: '4.0 SCORE',
            stickyType: 'RIGHT',
          },
          {
            type: 'TEXT_CELL',
            text: 'STATUS',
            stickyType: 'RIGHT',
          },
          {
            type: 'TEXT_CELL',
            text: 'DETAILS',
            align: 'center',
            stickyType: 'RIGHT',
          },
        ],
      },
      (item) => {
        return {
          type: 'ROW',
          isSelected: false,
          rowItem: item,
          cells: [
            {
              type: 'TEXT_LIST_CELL',
              text: [moment(item.updatedAt).format('h:mma'), moment(item.updatedAt).format('D MMMM YYYY')],
              widthInPx: 100,
            },
            {
              type: 'TEXT_CELL',
              text: item.metadata.supplierName,
              widthInPx: 100,
            },

            ...(isPlmExists
              ? [
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.plm,
                  } as TTableCellTree,
                ]
              : []),
            ...(stockType === TStockType.WITH_BCTX
              ? ([
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.buyer,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.po,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.style,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.materialCode,
                  },
                ] as TTableCellTree[])
              : ([
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.itemName,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.itemCode,
                  },
                  {
                    type: 'TEXT_CELL',
                    tag: {
                      tagContent:
                        item.lotGrnNumbers && item.lotGrnNumbers.length > 0
                          ? item.lotGrnNumbers.length > 1
                            ? `${item.lotGrnNumbers[0]} + ${item.lotGrnNumbers.length - 1} more`
                            : item.lotGrnNumbers[0]
                          : '--',
                      ...(item.lotGrnNumbers &&
                        item.lotGrnNumbers.length > 1 && {
                          tagTooltipContent: item.lotGrnNumbers.join(', '),
                        }),
                    },
                  },
                ] as TTableCellTree[])),

            ...(isPackedFilterAvailable
              ? [
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.packed ?? '--',
                  } as TTableCellTree,
                ]
              : []),

            {
              type: 'TEXT_CELL',
              text: item.metadata.fabricVariant,
            },
            ...(isColorFieldEnabled && stockType === TStockType.WITH_BCTX
              ? [
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.color,
                  } as TTableCellTree,
                ]
              : []),
            {
              type: 'TEXT_CELL',
              text: item.metadata.fabricColor,
            },
            {
              type: 'TEXT_CELL',
              text: item.metadata.fabricType,
            },
            {
              type: 'TEXT_CELL',
              text: item.metadata.lotNo,
            },
            {
              type: 'TEXT_CELL',
              text:
                (item.inspection.completedRollCount ? item.inspection.completedRollCount : 0) +
                '/' +
                item.inspection.selectedRollCount,
            },
            {
              type: 'TEXT_CELL',
              text: item.inspection && item.inspection.score !== null ? item.inspection.score : '--',
            },
            {
              type: 'TEXT_CELL',
              text: item.status,
            },
            {
              type: 'ACTION_CELL',
              actionType: 'VIEW',
            },
          ],
        };
      },
    );
  },
};
